import { isPlatformServer } from '@angular/common';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { catchError, filter, first, map, Observable, tap, throwError } from 'rxjs';

import { environment } from '@env/environment';

import { LOCATION, NAVIGATOR, REQUEST } from '@personal-area/common';
import { AuthServiceError } from '@personal-area/global/authorization/models';
import { AuthService } from '@personal-area/global/authorization/services';
import { EventType, GTMService } from '@personal-area/global/gtm';
import { CacheService, isConnectivityFailed } from '@personal-area/global/http/services';
import { I18nService } from '@personal-area/global/i18n';
import { NavigationService, RedirectBackService } from '@personal-area/global/navigation';
import { Role, rolesForHeader, UserServiceError } from '@personal-area/global/users/models';
import { UserFacade } from '@personal-area/global/users/services';
import {
  CAPTCHA_QUERY_PARAM,
  CaptchaService,
  RESUBMIT_FORM,
  RESUBMIT_QUERY_PARAM,
} from '@personal-area/shared/captcha';
import { LOCAL_STORAGE, UniversalStorageService } from '@personal-area/storage';

/**
 * Add token to all requests if logged in.
 * Process HTTP errors. Autorefresh token if expired.
 */
@Injectable()
export class RequestInterceptor implements HttpInterceptor {
  private readonly _req = inject(REQUEST);
  private readonly _navigator = inject(NAVIGATOR);
  private readonly _location = inject(LOCATION);
  private readonly _platformId = inject(PLATFORM_ID);
  private readonly _localStorage = inject(LOCAL_STORAGE);
  private readonly _gtmService = inject(GTMService);
  private readonly _cacheService = inject(CacheService);
  private readonly _authService = inject(AuthService);
  private readonly _i18nService = inject(I18nService);
  private readonly _navService = inject(NavigationService);
  private readonly _cookieService = inject(UniversalStorageService);
  private readonly _redirectBackService = inject(RedirectBackService);
  private readonly _userFacade = inject(UserFacade);
  private readonly _captchaService = inject(CaptchaService);

  public addHeaders(req: HttpRequest<any>): HttpRequest<any> {
    if (
      req.url.includes('accounts.google.com') ||
      req.url.includes('openidconnect.googleapis')
    ) {
      return req;
    }

    const user = this._userFacade.loggedUser;
    const host = this._req.protocol + '://' + this._req.hostname;

    let language = this._i18nService.language || user.language;
    language = language.includes('-') ? language.split('-')[0] : language;

    const roleForHeader = rolesForHeader[user.role] || user.role;

    const headers = {
      'X-User-Host': host,
      'X-User-Language': language,
      'X-User-Platform': 'website',
      'X-User-Role': roleForHeader || Role.Trader,
      'X-Application-Id': environment.apiData.clientID.toString(),
    };

    const fbcParam =
      this._cookieService.getItem('_fbc') ||
      this._localStorage.getItem('fbc_cookie') ||
      '';
    if (fbcParam) {
      headers['X-User-Facebook-Clickid'] = fbcParam;
    }

    if (this._cookieService.check('_fbp')) {
      headers['X-User-Facebook-Browserid'] =
        this._cookieService.getItem('_fbp') || '';
    }

    const contentType = req.headers.get('content-type');

    if (contentType) {
      headers['Content-Type'] = contentType;
    }

    return req.clone({
      setHeaders: headers,
    });
  }

  /**
   * Main interceptor function.
   */
  public intercept(req: HttpRequest<any>, next: HttpHandler): Observable<any> {
    return next.handle(this.addHeaders(req)).pipe(
      tap((event) => this._saveDataFromResponseHeaders(event)),
      catchError((err: HttpErrorResponse) => this.handleError(err, req, next)),
    );
  }

  /**
   * HTTP error handler.
   *
   * OAuth service does not send 401 code for invalid authorization token.
   * Thus we must check exception.code for 38005 value instead of err.status.
   */
  public handleError(
    err: HttpErrorResponse,
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<any> {
    if (isPlatformServer(this._platformId)) {
      console.log(`Handle server error ${req.url}, ${err}`);
      return throwError(() => err);
    }

    if (
      err.headers.get('Content-Type')?.startsWith('text/html') &&
      err.headers.has('cf-mitigated') &&
      [403, 429].includes(err.status)
    ) {
      this._captchaChallenge(req);
      return throwError(() => new HttpErrorResponse({
        error: {
          exception: {
            code: AuthServiceError.CaptchaChallenge,
          },
        },
        status: err.status,
      }));
    }

    if (err.status === 401) {
      return throwError(() => err);
    }

    const token = 'no-token';
    const exception = err.error
      ? err.error.exception || err.error.mapException
      : null;

    if (isConnectivityFailed(err)) {
      this._navService.openSnackBar();
    }

    if (exception) {
      if (exception.code === UserServiceError.RestrictedCountryException) {
        return throwError(() => err);
      } else if (
        exception.code === AuthServiceError.HeaderNotFoundException ||
        err.status === 401
      ) {
        return this._logout();
      }

      // eslint-disable-next-line no-prototype-builtins
      if (exception.hasOwnProperty('errors')) {
        err.error.mapException.token = token;
      } else {
        err.error.exception.token = token;
      }
    } else {
      err = new HttpErrorResponse({
        error: {
          exception: {
            code: 0,
            token: token,
          },
        },
        headers: err.headers,
        status: err.status,
        statusText: err.statusText,
        url: err.url,
      });
    }

    // return observable as error
    return throwError(() => err);
  }

  /**
   * Observable logout wrapper.
   */
  private _logout(): Observable<never> {
    if (isPlatformServer(this._platformId)) {
      this._authService.logout();
    } else {
      this._authService.logout(false);
      this._redirectBackService.redirectLogin();
    }

    return throwError(
      () =>
        new HttpErrorResponse({
          status: 401,
          error: 'logout',
        }),
    );
  }

  private async _captchaChallenge(req: HttpRequest<any>): Promise<void> {
    let oneTimeSecret = '';

    if (req.context.has(RESUBMIT_FORM)) {
      oneTimeSecret = this._captchaService.pushFormForResubmit(
        req.context.get(RESUBMIT_FORM),
      );
    }

    this._gtmService.event(EventType.CaptchaChallenge, {
      context: 'error',
      object: 'app',
      action: 'started',
      user_id: this._authService.userId,
      client_id: this._gtmService.currentGoogleClientId,
      client_time: (new Date()).toISOString()
    })

    // Prevent any requests from cached sources and let external service to
    // intercept request for index.html
    this._cacheService.clearCache();

    // SW doesn't exist in Firefox incognito
    if (navigator.serviceWorker) {
      for (const registration of await this._navigator.serviceWorker.getRegistrations()) {
        if (
          registration.active &&
          registration.active.scriptURL.includes('/cabinet')
        ) {
          await registration.unregister();
        }
      }
    }

    const newURL = new URL(this._location.toString());
    newURL.searchParams.set(CAPTCHA_QUERY_PARAM, 'true');

    if (oneTimeSecret) {  // for decipher after captcha
      newURL.searchParams.set(RESUBMIT_QUERY_PARAM, oneTimeSecret);
    }

    this._location.replace(newURL);
  }

  private _saveDataFromResponseHeaders(event: HttpEvent<unknown>): void {
    if (!(event instanceof HttpResponse) || this._authService.isLoggedIn || isPlatformServer(this._platformId)) {
      return;
    }

    if (event.headers.has('Date')) {
      this._authService.setServerTime(event.headers.get('Date'));
    }

    const isFromOffice = event.headers.get('X-User-Region') === 'office';
    const locationHeader = event.headers.get('X-User-Location');

    if (!isFromOffice && !locationHeader) {
      return;
    }

    this._userFacade.geoLocation$
      .pipe(
        first(),
        map(location => [
          location,
          isFromOffice || locationHeader.includes('--')
            ? environment.defaultLocation
            : locationHeader,
        ]),
        filter(([current, next]) => current !== next),
      )
      .subscribe(([, location]) =>
        this._userFacade.updateUserOptions({ location }),
      );
  }
}
